import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Checkt of het huidige pad een redirect heeft in `seo_redirects` en
 * voert die direct uit (replace, geen history-entry).
 *
 * Returns:
 *  - 'checking'  : we weten het nog niet
 *  - 'redirected': we hebben een redirect gevonden en de navigate is gestart
 *  - 'none'      : geen redirect, render gewoon de pagina
 */
export function useSeoRedirect(): "checking" | "redirected" | "none" {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"checking" | "redirected" | "none">("checking");

  useEffect(() => {
    let cancelled = false;
    setStatus("checking");

    const path = location.pathname;
    if (!path || path === "/") {
      setStatus("none");
      return;
    }

    (async () => {
      try {
        const { data } = await supabase
          .from("seo_redirects")
          .select("target_path")
          .eq("source_path", path)
          .maybeSingle();

        if (cancelled) return;

        if (data?.target_path && data.target_path !== path) {
          setStatus("redirected");
          navigate(data.target_path + location.search, { replace: true });
        } else {
          setStatus("none");
        }
      } catch {
        if (!cancelled) setStatus("none");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  return status;
}
